/**
 * @file     buyerMatchFlow.ts
 * @readme   ../../CLAUDE.md
 * @purpose  English strings for the purchasing power + neighbourhood
 *           matchmaker: five questions, hard-gated capture, sealed result,
 *           and the ranked-areas reveal.
 */
export const buyerMatchFlow = {
  page: {
    eyebrow: 'Buying in Metro Vancouver',
    title: 'What can you buy, and where should you be looking?',
    description:
      'Five questions turn your income, debts and down payment into a purchasing range at the federal qualifying rate, then rank all 21 Metro Vancouver municipalities against your budget and what you care about.',
    crumbHome: 'Home',
    crumbSelf: 'What you can buy',
    statQuestions: 'Questions',
    statCredit: 'Credit checks',
    statCreditValue: 'None',
    statCost: 'Cost to you',
    statAreas: 'Areas ranked'
  },
  actions: {
    continue: 'Continue'
  },
  incomeDebt: {
    title: 'What do you earn and owe?',
    description:
      'Pre-tax income for everyone on the mortgage. Debt is car, student, card and support payments.',
    incomeLabel: 'Annual household income',
    debtLabel: 'Monthly debt payments'
  },
  downPayment: {
    title: 'How much do you have for a down payment?',
    description: 'Savings and any confirmed family gift, not closing costs.',
    label: 'Available down payment'
  },
  propertyBeds: {
    title: 'What kind of home are you looking for?',
    typeLabel: 'Property type',
    bedsLabel: 'Bedrooms, minimum'
  },
  property: {
    condo: 'Condo',
    townhouse: 'Townhouse',
    detached: 'Detached',
    notSure: 'Not sure yet'
  },
  beds: {
    studio: 'Studio',
    one: '1',
    two: '2',
    threePlus: '3+'
  },
  location: {
    title: 'Where do you need to get to on weekdays?',
    description: 'Optional.',
    fieldLabel: 'City or area',
    placeholder: 'Downtown Vancouver'
  },
  priorities: {
    title: 'Pick the 3 that matter most',
    label: 'What matters in an area',
    cta: 'See what you can buy',
    commuteTransit: 'Commute & transit',
    schools: 'Schools',
    walkability: 'Walkability',
    safety: 'Safety',
    parks: 'Parks & green space',
    priceBudget: 'Price vs budget',
    familyProfessional: 'Family vs young professional',
    density: 'Detached vs condo density',
    newerEstablished: 'Newer vs established'
  },
  capture: {
    title: 'Your results are ready',
    description:
      'Confirm your email to see your range and ranked areas.',
    submit: 'See results',
    motivator: 'of 21 Metro Vancouver municipalities fit your budget and your three priorities.',
  },
  result: {
    title: 'Your purchasing range',
    sealedTitle: 'Check your inbox',
    sealedBody:
      'Open the link sent to {{email}} to see your results.',
    sealedChip: 'Sealed',
    rangeLabel: 'Your purchasing range',
    rangeAtLeast: '{{low}} or more',
    rangeNote:
      'Based on the federal stress-test rate and the middle of your income range.',
    statPayment: 'Monthly principal & interest',
    statRate: 'Qualifying rate',
    statAmortization: 'Amortization',
    statAmortizationValue: '{{years}} yrs',
    areasTitle: 'Where that budget goes furthest',
    areasSubtitle: 'All 21 areas ranked on your budget and your 3 picks: {{priorities}}.',
    strongOn: 'Strong on {{list}}',
    withinBudget: 'Within your purchasing range',
    stretch: 'a stretch at your budget',
    matchUnit: 'match',
    showAll: 'Show all 21 areas',
    showFewer: 'Show the top 5',
    seeListings: 'See listings in {{area}}',
    resend: 'Resend the email',
    useDifferentEmail: 'Use a different email',
    newEmailLabel: 'New email',
    sendLink: 'Send the link',
    resent: 'Confirmation email sent to {{email}}.',
    resendFailed: 'That did not go through. Try again in a minute.',
    disclaimer:
      'An estimate, not a mortgage approval or an offer of credit. The payment does not include property tax, strata fees, insurance or utilities.'
  }
};

/** Shared lead-flow chrome keys referenced by generic step components. */
export const leadFlow = {
  pills: {
    chosenCount: '{{count}} of {{max}} chosen'
  },
  progress: {
    stepsLeft: '{{count}} steps left',
    stepsLeft_one: '1 step left',
    almostDone: 'Almost done',
    stepPrefix: 'Step',
    count: '{{current}} of {{total}}'
  },
  sheet: {
    launch: 'Get started'
  },
  capture: {
    errors: {
      emailRequired: 'Enter your email address.',
      emailInvalid: 'Enter an email address, like name@example.com.'
    }
  },
  resumePrompt: {
    message: 'You have a session in progress.',
    resume: 'Resume from step {{step}}',
    startFresh: 'Start over'
  }
};
